import { useQuery } from '@tanstack/react-query';
import { TASK_WORKLOAD_TEXTS } from '@till-erp/collab/values';
import { apiFetch, ApiProblem, type StaffAbsent } from './api.ts';
import { useSession } from './session.tsx';
import { useToday } from './business-time.ts';

/**
 * Abwesenheit der Kolleginnen und Kollegen für die Aufgaben (Story 23.9, FR-144): nur „abwesend bis …“ aus der
 * staff-Abfrage `GET /api/v1/staff/calendar/absent`, nie Art oder Grund. apps/web setzt sie mit der Arbeitslast und dem
 * Zuweisen-Menü zusammen (collab liest staff nie, AD-1). Ohne Recht (403): still leer – die Aufgaben funktionieren ohne
 * Hinweis weiter, Zuweisen bleibt immer erlaubt. Andere Fehler wirft die Abfrage weiter (react-query wiederholt und
 * behält die zuletzt geladenen Daten).
 */

/** So viele Tage ab heute lädt die Seite auf einmal (4 Wochen Arbeitslast und Fälligkeiten der nächsten Wochen). */
export const ABSENCE_WINDOW_DAYS = 91;

const EMPTY: StaffAbsent['people'] = [];

function plusDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Zeitraum der Abfrage ab `today` (heute bis heute + 91 Tage). */
export function absenceWindow(today: string): { from: string; to: string } {
  return { from: today, to: plusDays(today, ABSENCE_WINDOW_DAYS) };
}

/** Lädt die Abwesenheiten; ohne Recht (403) → leere Liste, andere Fehler werden weitergeworfen. */
export async function loadAbsences(fetchAbsent: (path: string) => Promise<StaffAbsent>, from: string, to: string): Promise<StaffAbsent['people']> {
  try {
    return (await fetchAbsent(`/api/v1/staff/calendar/absent?from=${from}&to=${to}`)).people;
  } catch (error) {
    if (error instanceof ApiProblem && error.status === 403) return EMPTY;
    throw error;
  }
}

/** Abwesenheiten im Zeitraum; 403 → leere Liste; bei anderem Fehler die zuletzt geladenen (sonst leer, kein Fehler auf der Seite). */
export function useTeamAbsences(from: string, to: string, enabled = true): StaffAbsent['people'] {
  const { getToken } = useSession();
  const query = useQuery({
    queryKey: ['staff', 'calendar', 'absent', from, to],
    queryFn: () => loadAbsences((path) => apiFetch<StaffAbsent>(getToken, path), from, to),
    staleTime: 60_000,
    enabled,
  });
  return query.data ?? EMPTY;
}

/** Abwesenheiten ab heute (gemeinsamer Zeitraum für Arbeitslast, Blatt und Massenbearbeitung – eine Abfrage). */
export function useUpcomingAbsences(today?: string): StaffAbsent['people'] {
  const businessToday = useToday();
  const { from, to } = absenceWindow(today ?? businessToday);
  return useTeamAbsences(from, to);
}

/**
 * Zusätzlicher Zeitraum für einen Fälligkeitstag außerhalb des gemeinsamen Fensters (heute bis heute + 91 Tage), z. B.
 * überfällige Aufgaben oder Fälligkeit in ferner Zukunft (rein): der Tag selbst; im Fenster oder ohne Datum → `null`.
 */
export function dueDateWindow(today: string, date: string | null | undefined): { from: string; to: string } | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const shared = absenceWindow(today);
  return date < shared.from || date > shared.to ? { from: date, to: date } : null;
}

/**
 * Abwesenheiten für den Hinweis am Fälligkeitstag `date` (Blatt der Aufgabe): im gemeinsamen Fenster dieselbe Abfrage
 * wie Arbeitslast und Massenbearbeitung, sonst eine eigene Abfrage nur für diesen Tag.
 */
export function useAbsencesForDate(date: string | null | undefined): StaffAbsent['people'] {
  const today = useToday();
  const shared = useUpcomingAbsences(today);
  const extra = dueDateWindow(today, date);
  const own = useTeamAbsences(extra?.from ?? today, extra?.to ?? today, extra !== null);
  return extra ? own : shared;
}

/** `2027-02-26` → „26.02.“ */
function dayMonth(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

/** „abwesend bis 26.02.“ (offene Krankheit: „abwesend, Ende offen“), wenn die Person am Tag `date` abwesend ist, sonst `null` (rein). */
export function absentOn(people: StaffAbsent['people'], userId: string | null | undefined, date: string | null | undefined): string | null {
  if (!userId || !date) return null;
  const id = userId.toLowerCase();
  for (const p of people) {
    if (p.userId?.toLowerCase() !== id) continue;
    const range = p.ranges.find((r) => r.from <= date && date <= r.to);
    if (range) return range.openEnd ? TASK_WORKLOAD_TEXTS.absentOpenEnd : TASK_WORKLOAD_TEXTS.absentUntil(dayMonth(range.to));
  }
  return null;
}

/** Abwesende Tage der Person von `from` bis `to` (beide eingeschlossen), als Datumsmenge (rein). */
export function absentDays(people: StaffAbsent['people'], userId: string, from: string, to: string): Set<string> {
  const days = new Set<string>();
  const id = userId.toLowerCase();
  for (const p of people) {
    if (p.userId?.toLowerCase() !== id) continue;
    for (const r of p.ranges) {
      for (let d = r.from < from ? from : r.from; d <= r.to && d <= to; d = plusDays(d, 1)) days.add(d);
    }
  }
  return days;
}
